import { z } from "zod";

export const APP_BANNER_STATUSES = [
  "ACTIVE",
  "SCHEDULED",
  "EXPIRED",
  "INACTIVE",
  "PENDING_REVIEW",
  "REJECTED",
] as const;
export type AppBannerStatus = (typeof APP_BANNER_STATUSES)[number];

export const APP_BANNER_REVIEW_STATUSES = [
  "PENDING",
  "APPROVED",
  "REJECTED",
] as const;
export type AppBannerReviewStatus = (typeof APP_BANNER_REVIEW_STATUSES)[number];

export const appBannerReviewSchema = z.object({
  status: z.enum(APP_BANNER_REVIEW_STATUSES),
  reason: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});
export type AppBannerReview = z.infer<typeof appBannerReviewSchema>;

/** Relación de aspecto obligatoria del banner y tolerancia por redondeo del recorte. */
export const APP_BANNER_ASPECT_RATIO = 2;
export const APP_BANNER_ASPECT_TOLERANCE = 0.04;
export const APP_BANNER_MIN_WIDTH = 800;
export const APP_BANNER_MIN_HEIGHT = 400;
export const APP_BANNER_MAX = 10;

export const appBannerSchema = z.object({
  publicId: z.string(),
  imageKey: z.string(),
  imageUrl: z.string().nullable(),
  active: z.boolean(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  order: z.number(),
  status: z.enum(APP_BANNER_STATUSES),
  // Con default: si el front sale antes que el backend, la lista sigue
  // validando y los banners se leen como aprobados, que es como se comportaban.
  review: appBannerReviewSchema.default({
    status: "APPROVED",
    reason: null,
    reviewedAt: null,
  }),
});
export type AppBanner = z.infer<typeof appBannerSchema>;

export const appBannersResponseSchema = z.object({
  banners: z.array(appBannerSchema),
});
export type AppBannersResponse = z.infer<typeof appBannersResponseSchema>;

export interface CreateAppBannerInput {
  imageKey: string;
  active: boolean;
  startDate: string | null;
  endDate: string | null;
}

export interface UpdateAppBannerInput {
  active?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  order?: number;
}

export const appBannerFormSchema = z
  .object({
    startDate: z.string(),
    endDate: z.string(),
    active: z.boolean(),
  })
  .refine(
    ({ startDate, endDate }) => !startDate || !endDate || endDate >= startDate,
    {
      message: "La fecha de término no puede ser anterior a la de inicio",
      path: ["endDate"],
    },
  );
export type AppBannerFormValues = z.infer<typeof appBannerFormSchema>;
